import React, { useState, useEffect } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { RecurringContractDA } from '../../services/admin/recurring.contract.service';
import { TreasuryAccount } from '../../models/treasury.account.model';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { modalHide } from '../../utils/modal.util';
import { legendValid, legendInvalid } from '../../utils/legend.data.util';

import AppInputTreasuryAccount from '../element/InputTreasuryAccount';
import AppLegend from '../element/Legend';

export interface ModalDepositReceiveProps {
  companyForUser: any;
  treasuryAccountsForCompany: TreasuryAccount[];
  contract: any;
  setContract: React.Dispatch<React.SetStateAction<any>>;
}

let errorResponse: Error;

const AppModalDepositReceive: React.FunctionComponent<ModalDepositReceiveProps> = ({companyForUser, treasuryAccountsForCompany, contract, setContract}) => {
  const {lang} = StateLanguage()

  const [amount, setAmount] = useState<string>('')
  const [date, setDate] = useState<string>(new Date().toISOString().substring(0, 10))

  useEffect(() => {
    const prefill = contract?.security_deposit?.amount
    setAmount(prefill && prefill > 0 ? String(prefill) : '')
  }, [contract])
  const [treasuryAccount, setTreasuryAccount] = useState<{value: string, valid: boolean, object: any}>({value: '', valid: false, object: null})
  const [method, setMethod] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const resetForm = () => {
    setAmount('')
    setDate(new Date().toISOString().substring(0, 10))
    setTreasuryAccount({value: '', valid: false, object: null})
    setMethod('')
    setNote('')
  }

  const validateForm = (): boolean => {
    let valid = true
    const parsedAmount = parseFloat(amount)

    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      legendInvalid('legend-deposit-amount-required')
      valid = false
    } else {
      legendValid('legend-deposit-amount-required')
    }

    if (!date) {
      legendInvalid('legend-deposit-date-required')
      valid = false
    } else {
      legendValid('legend-deposit-date-required')
    }

    if (!treasuryAccount.valid) {
      legendInvalid('legend-treasury-account-required')
      valid = false
    }

    return valid
  }

  const submitReceiveDeposit = async () => {
    if (!companyForUser || !contract || !validateForm()) return

    setLoading(true)

    try {
      const data: any = {
        amount: parseFloat(amount),
        date: date,
        id_treasury_account: treasuryAccount.value,
      }
      if (method) data.method = method
      if (note) data.note = note

      const response = await RecurringContractDA.receiveDeposit(companyForUser.id, contract.id, data)

      if (response.status === 200) {
        setContract(response.data)
        resetForm()
        modalHide('modal-deposit-receive')
        GlobalSwal.fire({
          title: lang.labels.depositReceivedSuccess,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    } finally {
      setLoading(false)
    }
  }

  const currency = contract?.template?.currency?.code ?? 'CHF'

  return (
    <div id="modal-deposit-receive" className="modal fade" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered mw-650px">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="fw-bolder">{lang.labels.receiveDeposit}</h2>
            <div className="btn btn-icon btn-sm btn-active-icon-primary" data-bs-dismiss="modal">
              <span className="svg-icon svg-icon-1">
                <i className="bi bi-x-lg"></i>
              </span>
            </div>
          </div>
          <div className="modal-body scroll-y mx-5 my-3">
            <div className="mb-5">
              <label className="form-label required">{lang.labels.depositAmount} ({currency})</label>
              <input
                className="form-control"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{fontVariantNumeric: 'tabular-nums'}}
              />
              <AppLegend component={undefined} attribute={{check: !!amount && parseFloat(amount) > 0, name: "deposit-amount", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}} />
            </div>
            <div className="mb-5">
              <label className="form-label required">{lang.labels.date}</label>
              <input
                className="form-control"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <AppLegend component={undefined} attribute={{check: !!date, name: "deposit-date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}} />
            </div>
            <div className="mb-5">
              <label className="form-label required">{lang.labels.treasuryAccount}</label>
              <AppInputTreasuryAccount
                solid={false}
                treasuryAccountsForCompany={treasuryAccountsForCompany}
                treasuryAccount={treasuryAccount}
                setTreasuryAccount={setTreasuryAccount}
              />
            </div>
            <div className="mb-5">
              <label className="form-label">{lang.labels.depositMethod}</label>
              <input
                className="form-control"
                type="text"
                placeholder={lang.labels.depositMethod}
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              />
            </div>
            <div className="mb-5">
              <label className="form-label">{lang.labels.depositNote}</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder={lang.labels.depositNote}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-light" type="button" data-bs-dismiss="modal" onClick={resetForm}>{lang.labels.cancel}</button>
            <button className="btn btn-primary" type="button" disabled={loading} onClick={submitReceiveDeposit}>
              {loading ? lang.labels.pleaseWait : lang.labels.receiveDeposit}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalDepositReceive;
